import React from "react";
import { Flame } from "lucide-react";

const ADS = [
  {
    id: "nacho_pork_ad",
    bg: "/menu/nacho_pork_sandwich.png",
    badge: "NEW",
    accent: "#FFB400", // gold/jalapeño contrast
    headline: (
      <>
        Pork.
        <br />
        Cheese.
        <br />
        <span style={{ color: "#FFD700" }}>Heat.</span>
      </>
    ),
    name: "Nacho Jalapeño Pulled Pork Sandwich",
    tagline: "Cheese-soaked. Pepper-pop. No slaw.",
    price: 10,
  },
  {
    id: "japanese_pork_ad",
    bg: "/menu/japanese_pork_sandwich.png",
    badge: "NEW",
    accent: "#FF3B30",
    headline: (
      <>
        From Smoker
        <br />
        to <span style={{ color: "#FF3B30" }}>Tokyo</span>.
      </>
    ),
    name: "Japanese Pulled Pork Sandwich",
    tagline: "The classic — with a Tokyo twist.",
    price: 10,
  },
  {
    id: "nacho_pork_pizza_ad",
    bg: "/menu/nacho_pork_pizza.png",
    badge: "NEW",
    accent: "#FFB400",
    headline: (
      <>
        Pizza.
        <br />
        <span style={{ color: "#FFD700" }}>But</span>{" "}
        <span style={{ color: "#FF3B30" }}>Loaded</span>.
      </>
    ),
    name: "Nacho Jalapeño Pork Pizza",
    tagline: "Pizza, but with nacho cheese. No mistakes.",
    price: 14,
  },
];

function AdCard({ ad }) {
  return (
    <div
      data-testid={`ad-card-${ad.id}`}
      className="relative w-[540px] h-[540px] overflow-hidden bg-black"
    >
      {/* Background image */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${ad.bg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          filter: "saturate(1.15) contrast(1.05)",
        }}
      />
      {/* Dark gradient for legibility */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.2) 35%, rgba(0,0,0,0.85) 75%, rgba(0,0,0,0.97) 100%)",
        }}
      />
      {/* Ember vignette */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at 50% 0%, ${ad.accent}25, transparent 60%)`,
        }}
      />
      {/* Grain */}
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-25 mix-blend-screen pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/><feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.08 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      {/* Brand top-left */}
      <div className="absolute top-5 left-5 flex items-center gap-2">
        <Flame
          className="text-[#FF3B30] drop-shadow-[0_0_10px_rgba(255,59,48,0.7)]"
          style={{ width: 18, height: 18 }}
        />
        <span
          className="font-['Anton'] uppercase text-white tracking-[0.3em]"
          style={{ fontSize: 13 }}
        >
          Smokey <span className="text-[#FF3B30]">AF</span>
        </span>
      </div>

      {/* NEW badge top-right */}
      <div
        className="absolute top-5 right-5 font-['Anton'] uppercase tracking-widest px-3 py-1.5 rotate-3 shadow-[0_8px_24px_rgba(255,59,48,0.5)]"
        style={{
          fontSize: 15,
          backgroundColor: ad.accent,
          color: "#000",
        }}
      >
        {ad.badge}
      </div>

      {/* City of Fremont banner */}
      <div className="absolute top-[58px] left-0 right-0 text-center z-10 px-4">
        <div className="inline-flex items-center gap-2 bg-black/70 border border-[#FF8C00]/50 px-4 py-1.5 backdrop-blur">
          <span className="h-px w-6 bg-[#FF8C00]" />
          <span
            className="font-['Outfit'] uppercase text-[#FF8C00] tracking-[0.4em]"
            style={{ fontSize: 11 }}
          >
            City of Fremont
          </span>
          <span className="h-px w-6 bg-[#FF8C00]" />
        </div>
      </div>

      {/* Big headline */}
      <div className="absolute top-[120px] left-6 right-6 z-10">
        <h1
          className="font-['Anton'] uppercase text-white leading-[0.85] drop-shadow-[0_8px_25px_rgba(0,0,0,0.85)]"
          style={{ fontSize: 64, letterSpacing: "-0.02em" }}
        >
          {ad.headline}
        </h1>
      </div>

      {/* Bottom content */}
      <div className="absolute inset-x-0 bottom-0 px-6 pb-6 z-10">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <span
              className="font-['Outfit'] uppercase tracking-[0.35em] text-[#FF8C00]"
              style={{ fontSize: 10 }}
            >
              On the Menu
            </span>
            <h2
              className="font-['Anton'] uppercase text-white leading-[0.95] mt-1"
              style={{ fontSize: 26, letterSpacing: "-0.01em" }}
            >
              {ad.name}
            </h2>
            <p
              className="font-['Outfit'] italic text-zinc-300 mt-1"
              style={{ fontSize: 12 }}
            >
              {ad.tagline}
            </p>
          </div>
          <div
            className="bg-[#FF3B30] text-white font-['Anton'] tracking-wider px-3 py-2 rotate-3 shadow-[0_8px_24px_rgba(255,59,48,0.5)] flex-shrink-0"
            style={{ fontSize: 32 }}
          >
            ${ad.price}
          </div>
        </div>

        <div className="mt-4 flex items-center justify-between">
          <span
            className="font-['Anton'] tracking-widest uppercase bg-white text-black px-3 py-1.5"
            style={{ fontSize: 11 }}
          >
            Order Online
          </span>
          <span
            className="font-['Outfit'] uppercase text-zinc-400 tracking-[0.3em]"
            style={{ fontSize: 9 }}
          >
            smokey-af-bbq.emergent.host
          </span>
        </div>
      </div>
    </div>
  );
}

export default function NewItemAds() {
  return (
    <div className="min-h-screen bg-zinc-800 py-12 px-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="font-['Anton'] text-white text-3xl tracking-widest uppercase mb-2">
          Facebook Ads · New Items · 1080×1080
        </h1>
        <p className="font-['Outfit'] text-zinc-400 mb-8 text-sm">
          Click Download under each ad after the file regenerates.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
          {ADS.map((ad) => (
            <div key={ad.id} className="flex flex-col items-center gap-3">
              <AdCard ad={ad} />
              <a
                href={`/ads/${ad.id}.png`}
                download
                data-testid={`ad-dl-${ad.id}`}
                className="font-['Anton'] text-xs tracking-widest uppercase bg-[#FF3B30] hover:bg-white hover:text-black text-white px-5 py-2.5 transition-colors"
              >
                Download {ad.name}
              </a>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
